import React, { useCallback, useMemo, useRef, useState } from "react";
import { DollarsOnlyFormatter } from "@upserve/financials";
import { format } from "date-fns";
import { pipe, pluck, prop, reduceRight } from "ramda";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled, { withTheme } from "styles/styled_components";
import { IThemeInterface } from "styles/theme";
import { Maybe } from "true-myth";

import LaborCostTooltip from "components/labor/labor_cost_tooltip";
import { DateFormats } from "helpers/dates";
import { LaborCostsPerDay } from "helpers/insights/labor_costs";
import { categoryColors, ORDERED_CATEGORIES } from "helpers/schedule/roles";
import { ISalesForecast, ISalesSummary } from "types/account";
import { TOOLTIP_WIDTHS } from "components/shared/tooltip";

const StyledChartWrapper = styled.div`
  & .recharts-xAxis text {
    font-family: ${props => props.theme.text.mediumFamily};
    font-size: ${props => props.theme.text.size.base};
  }
  & .recharts-yAxis text {
    font-size: ${props => props.theme.text.size.small};
  }
`;

interface ILaborCostChart {
  costsPerDay: LaborCostsPerDay;
  salesForecast: Maybe<ISalesForecast>;
  theme: IThemeInterface;
}

const CHART_MARGINS = {
  top: 5,
  right: 10,
  left: 10,
  bottom: 5,
};
const TOOLTIP_POINTER_OFFSET = 75;

const DEFAULT_Y_AXIS = 3000;
const getMaxDailySales = pipe<
  ISalesForecast,
  ISalesSummary[],
  number[],
  number,
  number
>(
  prop("daily"),
  pluck("grossSalesAmount"),
  sales => Math.max(...sales),
  // Take 40% of max, rounded to nearest $100
  maxDaily => (maxDaily ? 100 * Math.round(maxDaily * 0.004) : DEFAULT_Y_AXIS)
);

interface ICoordData {
  isTooltipActive: boolean;
  activeCoordinate?: {
    x: number;
    y: number;
  };
}

interface IBarData {
  date: string;
  dateString: string;
  dayIndex: number;
  "Front of House": number;
  "Back of House": number;
  Other: number;
}

function LaborCostChart({
  costsPerDay,
  salesForecast,
  theme,
}: ILaborCostChart) {
  const costData: IBarData[] = useMemo(
    () => {
      const data: IBarData[] = [];
      let dayIndex = 0;
      costsPerDay.forEach((cost, dateString) => {
        data.push({
          date: format(dateString, DateFormats.WEEKDAY_DATE),
          dateString,
          dayIndex: dayIndex++,
          ...cost,
        });
      });
      return data;
    },
    [costsPerDay]
  );

  // Build the Bar category elements in right-to-left order.
  // The chart stacks them from bottom to top.
  const bars = reduceRight(
    (cat, barEls) =>
      barEls.concat(
        <Bar
          key={`bar-${cat}`}
          dataKey={cat}
          stackId="cost"
          fill={categoryColors[cat]}
        />
      ),
    [] as JSX.Element[],
    ORDERED_CATEGORIES
  );

  const [pointer, setPointer] = useState<"left" | "right">("left");
  const [tooltipXPos, setTooltipXPos] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const onMouseMove = useCallback(
    (coordData: ICoordData) => {
      if (coordData.isTooltipActive && coordData.activeCoordinate) {
        let newX = Math.round(
          coordData.activeCoordinate.x + TOOLTIP_POINTER_OFFSET
        );
        let svgWidth = 0;
        if (wrapperRef.current) {
          const chartSVG = wrapperRef.current.querySelector("svg");
          if (chartSVG) {
            svgWidth = chartSVG.getBoundingClientRect().width;
          }
        }
        const tooltipWidth = salesForecast.isJust()
          ? TOOLTIP_WIDTHS.large
          : TOOLTIP_WIDTHS.medium;
        // Check if tooltip would be clipped off the chart.
        // If so, switch it to displaying left of the bar (with pointer "right")
        if (newX + tooltipWidth > svgWidth) {
          newX -= tooltipWidth + TOOLTIP_POINTER_OFFSET * 2;
          setPointer("right");
        } else {
          setPointer("left");
        }
        setTooltipXPos(newX);
      }
    },
    [salesForecast]
  );

  const tooltipPos = useMemo(
    () => ({
      x: tooltipXPos,
      y: salesForecast.isJust() ? 30 : 12,
    }),
    [tooltipXPos, salesForecast]
  );

  const yAxisMax = useMemo(
    () =>
      salesForecast.match({
        Just: getMaxDailySales,
        Nothing: () => DEFAULT_Y_AXIS,
      }),
    [salesForecast]
  );

  return (
    <StyledChartWrapper ref={wrapperRef}>
      <ResponsiveContainer height={300} width="100%">
        <BarChart
          barCategoryGap="28%"
          data={costData}
          margin={CHART_MARGINS}
          onMouseMove={onMouseMove}>
          <CartesianGrid color={theme.colors.dsGrayLighter} vertical={false} />
          <XAxis dataKey="date" tickLine={false} />
          <YAxis
            domain={[0, yAxisMax]}
            interval="preserveStartEnd"
            tickLine={false}
            tickFormatter={DollarsOnlyFormatter.format}
          />
          <Tooltip
            content={
              <LaborCostTooltip
                costsPerDay={costsPerDay}
                pointer={pointer}
                salesForecast={salesForecast}
              />
            }
            cursor={false}
            isAnimationActive={false}
            position={tooltipPos}
          />
          {bars}
        </BarChart>
      </ResponsiveContainer>
    </StyledChartWrapper>
  );
}

export default withTheme(LaborCostChart);
